import React from 'react'
import Link from 'next/link'
import { Swiper, SwiperSlide } from 'swiper/react'
import { FreeMode, Mousewheel } from 'swiper/modules'
import ImageComponent from '@/src/components/common/ImageComponent'
import AddToWishlist from '@/src/components/page-components/products/AddToWishlist'
import { ArrowUpRightSVG, SearchSVG } from '@/src/ui/AllSVG'
import { getCategoriesPath, getSingleProductPath } from '@/src/routes/routes'
import type { INewArrivalsAPIResponseDataEntity } from '@/src/types/newArrivals'
import type { ISearchSuggestionsAPIResponseDataSuggestionsEntity } from '@/src/types/searchSuggestions'
import QueryResultProductsSkeleton from '@/src/ui/skeletons/QueryResultProductCardSkeleton'
import SearchSuggestionsSkeleton from '@/src/ui/skeletons/SearchSuggestionsSkeleton'

interface QueryResultProps {
    query: string
    onClose: () => void
    onSuggestionClick: (tag: string) => void
    searchSuggestions: ISearchSuggestionsAPIResponseDataSuggestionsEntity[]
    isSuggestionsLoading: boolean
    searchProducts: INewArrivalsAPIResponseDataEntity[]
    searchTotalCount?: number
    isSearchLoading: boolean
}

function SuggestionLabel({ name, trimmed }: { name: string; trimmed: string }) {
    const i = name.toLowerCase().indexOf(trimmed)
    if (trimmed.length === 0 || i < 0) {
        return <span className="text-body-color/70">{name}</span>
    }
    return (
        <span>
            <span className="font-semibold text-body-color">{name.slice(0, i)}</span>
            <span className="font-semibold text-body-color">
                {name.slice(i, i + trimmed.length)}
            </span>
            <span className="text-body-color/50">{name.slice(i + trimmed.length)}</span>
        </span>
    )
}

export default function QueryResult({
    query,
    onClose,
    onSuggestionClick,
    searchSuggestions,
    isSuggestionsLoading,
    searchProducts,
    searchTotalCount,
    isSearchLoading,
}: QueryResultProps) {
    const trimmed = query.trim().toLowerCase()

    const matched = searchProducts

    const productCountLabel =
        searchTotalCount !== undefined ? searchTotalCount : matched.length

    const showSuggestions =
        isSuggestionsLoading || (searchSuggestions && searchSuggestions?.length > 0)

    return (
        <div
            className={
                showSuggestions
                    ? 'grid grid-cols-1 md:grid-cols-8 gap-4 md:items-start md:gap-0'
                    : 'space-y-3'
            }
        >
            {/* Keyword suggestions */}
            {showSuggestions && (
                <div className="col-span-2 space-y-3 md:shrink-0 md:border-r md:border-muted-color md:pr-6 lg:pr-8">
                    <h3 className="text-sm font-semibold tracking-[0.14px] uppercase">Suggestions</h3>
                    {isSuggestionsLoading ? (
                        <SearchSuggestionsSkeleton count={4} />
                    ) : (
                        <ul className="space-y-2 max-h-[min(50vh,22rem)] overflow-y-auto pr-1 md:max-h-[min(60vh,28rem)]">
                            {searchSuggestions && searchSuggestions?.length > 0 && searchSuggestions?.map((s, index) => (
                                <li key={index}>
                                    {s.type === 'product' ? (
                                        <Link
                                            href={getSingleProductPath(s?.slug ?? '')}
                                            onClick={onClose}
                                            className="flex items-center gap-2 text-sm text-body-color/70 hover:text-body-color transition-colors"
                                        >
                                            <SearchSVG />
                                            <SuggestionLabel name={s?.name ?? ''} trimmed={trimmed} />
                                        </Link>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => onSuggestionClick(s?.name ?? '')}
                                            className="flex items-center gap-2 text-sm text-body-color/70 hover:text-body-color transition-colors cursor-pointer"
                                        >
                                            <SearchSVG />
                                            <SuggestionLabel name={s?.name ?? ''} trimmed={trimmed} />
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {/* Matched products */}
            <div className={`space-y-3 min-w-0 ${showSuggestions ? 'flex-1 md:pl-6 lg:pl-8 col-span-6' : 'col-span-8'}`}>
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                    <h3 className="text-sm font-semibold tracking-[0.14px] uppercase">
                        {isSearchLoading
                            ? 'Products'
                            :matched && matched?.length > 0
                              ? `Products (${productCountLabel})`
                              : 'No results found'}
                    </h3>
                    {query?.trim() && (
                        <Link
                            href={`${getCategoriesPath()}?search=${query?.trim()}`}
                            onClick={onClose}
                            className="flex shrink-0 items-center gap-1 text-sm tracking-[0.14px] uppercase text-accent-color underline underline-offset-4 hover:text-accent-color/80 transition-colors"
                        >
                            View all
                            <ArrowUpRightSVG className="w-4 h-4" />
                        </Link>
                    )}
                </div>
                {isSearchLoading ? (
                    <QueryResultProductsSkeleton count={6} />
                ) : matched && matched?.length > 0 ? (
                    <Swiper
                        modules={[FreeMode, Mousewheel]}
                        slidesPerView={2.2}
                        spaceBetween={12}
                        freeMode={{ enabled: true, momentum: true, momentumRatio: 0.4 }}
                        mousewheel={{ forceToAxis: true, releaseOnEdges: true }}
                        breakpoints={{
                            480: { slidesPerView: 3 },
                            768: { slidesPerView: showSuggestions ? 2.5 : 3.5 },
                            1024: { slidesPerView: showSuggestions ? 3.5 : 4.5 },
                        }}
                    >
                        {matched && matched?.length > 0 && matched?.map((product) => (
                            <SwiperSlide key={product?._id}>
                                <Link
                                    href={getSingleProductPath(product?.slug ?? '')}
                                    onClick={onClose}
                                    className="group space-y-3 cursor-pointer block"
                                >
                                    <div className="relative aspect-4/5 w-full bg-secondary-background-color rounded-xl overflow-hidden">
                                        <ImageComponent
                                            src={product?.variants?.[0]?.images?.[0] ?? product?.thumbnail ?? ''}
                                            alt={product?.title ?? ''}
                                            object_cover={false}
                                            priority={false}
                                        />
                                        <div className="wishlist_btn absolute top-3 right-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                            <AddToWishlist product_id={product?._id} isAlreadyWishlist={product?.alreadyWishlist} />
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-xs font-medium truncate">{product?.title ?? ''}</p>
                                        <p className="text-[10px] tracking-[0.14px] uppercase flex items-center gap-1 underline underline-offset-2">
                                            Explore Now
                                            <ArrowUpRightSVG className="text-body-color" />
                                        </p>
                                    </div>
                                </Link>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                ) : (
                    <p className="text-sm text-body-color/50">
                        Try searching for something else, like &quot;Outerwear&quot; or &quot;Tops&quot;.
                    </p>
                )}
            </div>
        </div>
    )
}
